import type { LogProps } from "@cabal/shared/util/seq-log";
import { LogLevel } from "@cabal/shared/util/seq-log";
import { emitSeqLog } from "@/hooks/telemetry/use-seq-log";

export { LogLevel };

export const CHAT_UNREAD_DIAGNOSTIC_INSTANCE_ID =
  "chat-unread-readmark-2026-06-27";

export enum ChatUnreadDiagnosticComponent {
  ChatPanelScrollActions = "ChatPanelScrollActions",
  ChatPanelScrollEffects = "ChatPanelScrollEffects",
  SessionChatModeActions = "SessionChatModeActions",
  SessionPageMarkReadUpToHandler = "SessionPageMarkReadUpToHandler",
  SessionReadMarkerActions = "SessionReadMarkerActions",
  SessionReadMarkers = "SessionReadMarkers",
  UseMarkChatRead = "UseMarkChatRead",
}

export enum ChatUnreadDiagnosticEvent {
  AutoReadMarker = "chat-unread.auto-read-marker",
  BottomReadMarker = "chat-unread.bottom-read-marker",
  ChatModeReadMarker = "chat-unread.chat-mode-read-marker",
  LocalReadMarker = "chat-unread.local-read-marker",
  MarkChatReadMutation = "chat-unread.mark-chat-read-mutation",
  MarkReadUpToHandler = "chat-unread.mark-read-up-to-handler",
  ScrollReadTarget = "chat-unread.scroll-read-target",
}

export enum ChatUnreadDiagnosticPhase {
  CancelDelayedMainChatMarkRead = "cancel-delayed-main-chat-mark-read",
  Dispatch = "dispatch",
  EvaluateDelayedMainChatMarkRead = "evaluate-delayed-main-chat-mark-read",
  EvaluateScrollReadTarget = "evaluate-scroll-read-target",
  EvaluateSessionStatusMarkRead = "evaluate-session-status-mark-read",
  FireDelayedMainChatMarkRead = "fire-delayed-main-chat-mark-read",
  FireSessionStatusMarkRead = "fire-session-status-mark-read",
  InvalidateMainChatReadQueries = "invalidate-main-chat-read-queries",
  LoadProductCollabsReject = "load-product-collabs-reject",
  LoadProductCollabsResolve = "load-product-collabs-resolve",
  LoadProductCollabsSkipCached = "load-product-collabs-skip-cached",
  LoadProductCollabsStart = "load-product-collabs-start",
  MarkAllChatEvaluate = "mark-all-chat-evaluate",
  MarkAllProductCollabsEvaluate = "mark-all-product-collabs-evaluate",
  MarkChatReadUpToEvaluate = "mark-chat-read-up-to-evaluate",
  MarkLeavingChatAsRead = "mark-leaving-chat-as-read",
  MarkProductCollabsReadReject = "mark-product-collabs-read-reject",
  MarkProductCollabsReadResolve = "mark-product-collabs-read-resolve",
  MarkProductCollabsReadStart = "mark-product-collabs-read-start",
  MarkProductCollabsReadUpToEvaluate = "mark-product-collabs-read-up-to-evaluate",
  NoScrollReadTarget = "no-scroll-read-target",
  RejectServerMarkAllMainChatRead = "reject-server-mark-all-main-chat-read",
  ResolveBottomVisibleReadMarker = "resolve-bottom-visible-read-marker",
  ResolveServerMarkAllMainChatRead = "resolve-server-mark-all-main-chat-read",
  ScheduleBottomVisibleReadMarker = "schedule-bottom-visible-read-marker",
  SelectScrollReadTarget = "select-scroll-read-target",
  SetChatModeNoProductTarget = "set-chat-mode-no-product-target",
  SetChatModeStart = "set-chat-mode-start",
  SkipNoMarkReadHandler = "skip-no-mark-read-handler",
  StartServerMarkAllMainChatRead = "start-server-mark-all-main-chat-read",
}

export enum ChatUnreadDiagnosticBranch {
  Invoke = "invoke",
  InvokeServerOnly = "invoke-server-only",
  MainChat = "main-chat",
  MarkAllMainChat = "mark-all-main-chat",
  MarkAllProductChat = "mark-all-product-chat",
  MarkRead = "mark-read",
  NoReadableScope = "no-readable-scope",
  ProductChat = "product-chat",
  Schedule = "schedule",
  Skip = "skip",
  SkipEmptyProductCollabs = "skip-empty-product-collabs",
  SkipMissingTarget = "skip-missing-target",
  SkipNoLatestCollab = "skip-no-latest-collab",
  SkipNoLatestMessage = "skip-no-latest-message",
  Update = "update",
}

export enum ChatUnreadDiagnosticBlocker {
  LoadingChat = "loading-chat",
  MainChatNotReadable = "main-chat-not-readable",
  NoDisplayMessages = "no-display-messages",
  NoUnreadMainChat = "no-unread-main-chat",
  NotMainChat = "not-main-chat",
}

enum ChatUnreadDiagnosticContextValue {
  Feature = "chat-unread-readmark",
  Scope = "chat-unread-diagnostic",
}

interface LogChatUnreadDiagnosticInput {
  component: ChatUnreadDiagnosticComponent;
  event: ChatUnreadDiagnosticEvent;
  props?: LogProps;
  level?: LogLevel;
}

interface CaptureChatUnreadElementSnapshotInput {
  element: HTMLElement | null;
  container: HTMLElement | null;
}

export function logChatUnreadDiagnostic({
  component,
  event,
  props,
  level = LogLevel.Info,
}: LogChatUnreadDiagnosticInput): void {
  emitSeqLog({
    component,
    msg: event,
    level,
    context: {
      feature: ChatUnreadDiagnosticContextValue.Feature,
      instanceId: CHAT_UNREAD_DIAGNOSTIC_INSTANCE_ID,
      scope: ChatUnreadDiagnosticContextValue.Scope,
    },
    props,
  });
}

export function captureChatUnreadElementSnapshot({
  element,
  container,
}: CaptureChatUnreadElementSnapshotInput): LogProps {
  if (!element) {
    return {
      exists: false,
    };
  }

  const rect = element.getBoundingClientRect();
  const containerRect = container?.getBoundingClientRect() ?? null;
  const relativeTop = containerRect ? rect.top - containerRect.top : null;
  const relativeBottom = containerRect ? rect.bottom - containerRect.top : null;
  const intersectsContainer = containerRect
    ? rect.bottom > containerRect.top && rect.top < containerRect.bottom
    : null;

  return {
    exists: true,
    isConnected: element.isConnected,
    top: roundDiagnosticNumber(rect.top),
    bottom: roundDiagnosticNumber(rect.bottom),
    height: roundDiagnosticNumber(rect.height),
    relativeTop: roundDiagnosticNullableNumber(relativeTop),
    relativeBottom: roundDiagnosticNullableNumber(relativeBottom),
    intersectsContainer,
  };
}

export function normalizeChatUnreadError(error: unknown): LogProps {
  if (error instanceof Error) {
    return {
      errorName: error.name,
      errorMessage: error.message,
      errorStack: error.stack ?? null,
    };
  }

  return {
    errorValue: String(error),
  };
}

function roundDiagnosticNullableNumber(value: number | null): number | null {
  if (value == null) {
    return null;
  }

  return roundDiagnosticNumber(value);
}

function roundDiagnosticNumber(value: number): number {
  return Math.round(value * 100) / 100;
}
